/// @ts-check
/**
 * Storage Accessor - 頁面層級的 storage 數據提取工具
 * 
 * 這個模組負責從 sessionStorage/localStorage 中提取數據
 * 然後傳遞給核心模組，保持核心模組的純粹性和可測試性
 * 
 * 架構原則：
 * - Storage 訪問只在頁面層級發生
 * - 核心模組接收數據作為參數
 * - 便於測試和未來更換數據來源
 */

/**
 * @typedef {Object} StorageData
 * @property {string} activityTitle - 活動標題
 * @property {string} activityId - 活動 ID
 * @property {string} groupId - 群組 ID
 * @property {string} currentUser - 當前用戶名稱
 * @property {string} currentPath - 當前路徑
 */

/**
 * 從 storage 中提取所有相關數據
 * @returns {StorageData} storage 數據對象
 */
export const getStorageData = () => {
  return {
    activityTitle: getActivityTitle(),
    activityId: getActivityId(),
    groupId: getGroupId(),
    currentUser: getCurrentUser(),
    currentPath: getCurrentPath()
  };
};

/**
 * 獲取活動標題
 * @returns {string} 活動標題
 */
export const getActivityTitle = () => {
  return sessionStorage.getItem('activityTitle') || 
         localStorage.getItem('activityTitle') || 
         '未命名活動';
};

/**
 * 獲取活動 ID
 * @returns {string} 活動 ID
 */
export const getActivityId = () => {
  return sessionStorage.getItem('activityId') || '';
};

/**
 * 獲取群組 ID
 * @returns {string} 群組 ID
 */
export const getGroupId = () => {
  return sessionStorage.getItem('groupId') || '';
};

/**
 * 獲取當前用戶名稱
 * @returns {string} 用戶名稱
 */
export const getCurrentUser = () => {
  return localStorage.getItem('userName') || 
         sessionStorage.getItem('displayName') ||
         sessionStorage.getItem('userId') || 
         '匿名用戶';
};

/**
 * 獲取當前路徑（用於模式檢測）
 * @returns {string} 當前路徑
 */
export const getCurrentPath = () => {
  return typeof window !== 'undefined' ? window.location.pathname : '';
};

/**
 * 檢查必要的 storage 數據是否存在
 * @returns {{isValid: boolean, missing: string[]}} 驗證結果
 */
export const validateStorageData = () => {
  const missing = [];
  
  if (!getActivityId()) {
    missing.push('activityId');
  }
  
  if (!getGroupId()) {
    missing.push('groupId');
  }
  
  return {
    isValid: missing.length === 0,
    missing
  };
};

/**
 * 獲取用於 context 構建的參數對象
 * @returns {{activityTitle: string, activityId: string, groupId: string, currentUser: string, currentPath: string}}
 */
export const getContextBuildParams = () => {
  return getStorageData();
};

/**
 * 獲取用於夥伴回應觸發的參數
 * @returns {{activityId: string, groupId: string}}
 */
export const getPartnerResponseParams = () => {
  return {
    activityId: getActivityId(),
    groupId: getGroupId()
  };
};

export default {
  getStorageData,
  getActivityTitle,
  getActivityId,
  getGroupId,
  getCurrentUser,
  getCurrentPath,
  validateStorageData,
  getContextBuildParams,
  getPartnerResponseParams
};

